import React, { useState } from 'react';
import { Bookmark, Plus, Check, Trash2, Layout, Sparkles, X } from 'lucide-react';
import { SmartView, FilterRule, SortRule } from '../types';

interface SmartViewSelectorProps {
  smartViews: SmartView[];
  activeViewId: string | null;
  onSelectView: (view: SmartView | null) => void;
  onSaveCurrentView: (name: string) => void;
  onDeleteView: (id: string) => void;
  hasActiveFilters: boolean;
}

export default function SmartViewSelector({
  smartViews,
  activeViewId,
  onSelectView,
  onSaveCurrentView,
  onDeleteView,
  hasActiveFilters,
}: SmartViewSelectorProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const handleSave = () => {
    if (!newViewName.trim()) return;
    onSaveCurrentView(newViewName.trim());
    setNewViewName('');
    setIsSaving(false);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mr-1">
          <Bookmark className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Smart Views:
        </span>

        {/* All Leads (Default Default View) */}
        <button
          onClick={() => onSelectView(null)}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
            activeViewId === null
              ? 'bg-blue-600 text-white shadow-xs'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100'
          }`}
        >
          <Layout className="w-3 h-3" /> All Leads (Standard)
        </button>

        {/* Custom Smart Views */}
        {smartViews.map((view) => (
          <div key={view.id} className="inline-flex items-center group">
            <button
              onClick={() => onSelectView(view)}
              className={`px-3 py-1.5 rounded-l-lg text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
                activeViewId === view.id
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100'
              }`}
            >
              <Sparkles className="w-3 h-3" /> {view.name}
            </button>
            <button
              onClick={() => onDeleteView(view.id)}
              className={`p-1.5 rounded-r-lg border-y border-r text-xs transition cursor-pointer ${
                activeViewId === view.id
                  ? 'bg-blue-700 text-blue-100 border-blue-600 hover:bg-blue-800'
                  : 'bg-white dark:bg-slate-800 text-slate-400 hover:text-rose-500 border-slate-200 dark:border-slate-700'
              }`}
              title="Delete Saved View"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Save Current View Action */}
      {hasActiveFilters && (
        <div>
          {isSaving ? (
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-800 p-1 rounded-lg border border-blue-300 dark:border-blue-700">
              <input
                type="text"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="View Name..."
                className="text-xs font-semibold px-2 py-1 rounded bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none w-36"
                autoFocus
              />
              <button
                onClick={handleSave}
                className="p-1 rounded bg-blue-600 text-white hover:bg-blue-700 transition cursor-pointer"
                title="Confirm Save View"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setIsSaving(false)}
                className="p-1 rounded text-slate-400 hover:text-slate-600 transition cursor-pointer"
                title="Cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsSaving(true)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-950/80 hover:bg-blue-200 transition cursor-pointer border border-blue-200 dark:border-blue-800"
            >
              <Plus className="w-3.5 h-3.5" /> Save Current as Smart View
            </button>
          )}
        </div>
      )}
    </div>
  );
}
